import { Badge, Input } from '@hub-la/shadcn'
import { X } from 'lucide-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FilterArrayByEmailValidation } from '../../usecases/filter-array-by-email-validation'

type EmailsAutocompleteProps = {
  helperText?: string | null
  error?: boolean
  value: string[] | never[]
  setValue: (value: string[]) => void
}

const filterArrayByEmailValidation = new FilterArrayByEmailValidation()

const EmailsAutocomplete: React.FC<EmailsAutocompleteProps> = ({ error, helperText, value, setValue }) => {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [emailHelperText, setEmailHelperText] = useState<string | null>(null)

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email.trim())
  }

  const handlePasteOnInput = (text: string) => {
    const emailsWithComma = text.split(',')
    const emailsWithNewLine = text.split('\n')
    const emails = [...emailsWithComma, ...emailsWithNewLine].filter(
      (email, index, self) => self.indexOf(email) === index,
    )

    const emailsArray = filterArrayByEmailValidation.execute(emails, value)

    setValue([...value, ...emailsArray])
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    // when type comma add the email
    if (emailError) {
      setEmailError(false)
      setEmailHelperText(null)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      const emails = inputValue
        .replace(/[\n\s]+/g, ',')
        .split(',')
        .map((email) => email.trim())
        .filter((email) => email !== '')

      const invalidEmails = emails.filter((email) => !isValidEmail(email))
      if (invalidEmails.length > 0) {
        setEmailError(true)
        setEmailHelperText(t('addFreeMembersModal.inputs.emails.invalidEmail'))
        return
      }

      const validEmails = emails.filter(isValidEmail)
      if (validEmails.length > 0) {
        const newEmails = filterArrayByEmailValidation.execute(validEmails, value)
        setValue([...value, ...newEmails])
        setInputValue('')
      }
    }
  }

  const handleRemoveEmail = (emailToRemove: string) => {
    const updatedEmails = value.filter((email) => email !== emailToRemove)
    setValue(updatedEmails)
  }

  return (
    <div>
      <Input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onPaste={(e) => {
          e.preventDefault()
          handlePasteOnInput(e.clipboardData.getData('text'))
        }}
        onBlur={(e) => {
          e.preventDefault()
          setInputValue('')
          handlePasteOnInput(e.target.value)
        }}
        className="w-full p-2 border rounded-md"
      />
      <div className="flex flex-wrap gap-2 mt-2">
        {value.map((email, index) => (
          <Badge key={index} variant="secondary" className="flex items-center gap-1 px-2 py-1">
            {email}
            <button
              onClick={() => handleRemoveEmail(email)}
              className="text-muted-foreground hover:text-secondary-foreground focus:outline-none"
              aria-label={t('addFreeMembersModal.inputs.emails.removeEmail')}
            >
              <X size={14} />
            </button>
          </Badge>
        ))}
      </div>
      <p className={`text-sm ${emailError ? 'text-destructive' : 'text-muted-foreground'}`}>
        {emailError && emailHelperText}
      </p>
    </div>
  )
}

export { EmailsAutocomplete }
